import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import * as Sentry from '@sentry/tanstackstart-react';
import { create } from 'zustand';

import {
  getCachedNativeToken,
  getNativeAuthUserId,
  isCurrentNativeSession,
  nativeSessionMark,
} from '#features/api/utils/nativeAuth';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import { useVoiceStore, type VoiceRoomInfo } from '#features/voice/model/voiceStore';
import { envConfig } from '#constants/envConfig';

export type NativeVoiceSessionSnapshot = {
  available: boolean;
  authUserId: string | null;
  sessionId: string | null;
  postId: string | null;
  hostId: string | null;
  generation: number;
  revision: number;
  phase: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'ended';
  micEnabled: boolean;
  muted: boolean;
  micDenied: boolean;
  connectedAt: number | null;
  members: {
    userId: string;
    connected: boolean;
    speaking: boolean;
    muted: boolean;
    displayName: string | null;
    avatar: string | null;
    volume?: number;
    playbackMuted?: boolean;
  }[];
  errorCode: string | null;
  endReason: string | null;
};

type NativeRoomTarget = { sessionId: string; generation?: number };
interface NativeVoiceSessionPlugin {
  available(): Promise<{ available: boolean }>;
  getState(): Promise<NativeVoiceSessionSnapshot>;
  syncAuth(): Promise<NativeVoiceSessionSnapshot>;
  connect(options: {
    sessionId: string;
    postId: string;
    hostId: string | null;
    livekitUrl: string;
    token: string;
    muted: boolean;
  }): Promise<NativeVoiceSessionSnapshot>;
  disconnect(options: NativeRoomTarget): Promise<NativeVoiceSessionSnapshot>;
  setMuted(options: NativeRoomTarget & { muted: boolean }): Promise<NativeVoiceSessionSnapshot>;
  setParticipantAudio(
    options: NativeRoomTarget & { userId: string; muted: boolean; volume: number }
  ): Promise<NativeVoiceSessionSnapshot>;
  addListener(
    event: 'stateChanged',
    listener: (state: NativeVoiceSessionSnapshot) => void
  ): Promise<PluginListenerHandle>;
}

// A Capacitor proxy must never be the value resolved by a Promise (it has a synthetic `then`).
let plugin: NativeVoiceSessionPlugin | undefined;
const nativePlugin = () =>
  (plugin ??= registerPlugin<NativeVoiceSessionPlugin>('NativeVoiceSession'));
export const isNativeVoiceSessionCandidate = () =>
  envConfig.IS_NATIVE &&
  Capacitor.getPlatform() === 'ios' &&
  Capacitor.isPluginAvailable('NativeVoiceSession');

export const useNativeVoiceSession = create<{
  mode: 'pending' | 'native' | 'web';
  ready: boolean;
  snapshot: NativeVoiceSessionSnapshot | null;
}>(() => ({
  mode: isNativeVoiceSessionCandidate() ? 'pending' : 'web',
  ready: !isNativeVoiceSessionCandidate(),
  snapshot: null,
}));

let authEpoch = 0;
let syncEpoch = 0;
let ownerUserId: string | null = null;
let displayedOwnerUserId: string | null = null;
let availability: Promise<boolean> | undefined;
const leaving = new Set<string>();
const stopping = new Map<string, Promise<void>>();

/** A failed read cannot enable a second, browser-owned room while native media may be live. */
export async function resolveNativeVoiceSessionSupport(): Promise<boolean> {
  if (!isNativeVoiceSessionCandidate()) {
    useNativeVoiceSession.setState({ mode: 'web', ready: true, snapshot: null });
    return false;
  }
  availability ??= nativePlugin()
    .available()
    .then(
      ({ available }) => {
        useNativeVoiceSession.setState({ mode: available ? 'native' : 'web', ready: !available });
        return available;
      },
      (error: unknown) => {
        availability = undefined;
        if ((error as { code?: string })?.code === 'UNIMPLEMENTED') {
          useNativeVoiceSession.setState({ mode: 'web', ready: true, snapshot: null });
          return false;
        }
        throw error;
      }
    );
  return availability;
}

/** Invalidate pending replies before another token or account can hydrate the voice UI. */
export function invalidateNativeVoiceSessionAuth() {
  ++authEpoch;
  ownerUserId = null;
  if (isNativeVoiceSessionCandidate()) {
    useNativeVoiceSession.setState({ ready: false, snapshot: null });
  }
}

export function applyNativeVoiceSessionSnapshot(snapshot: NativeVoiceSessionSnapshot): boolean {
  if (
    !ownerUserId ||
    snapshot.authUserId !== ownerUserId ||
    !isCurrentNativeSession(getCachedNativeToken())
  )
    return false;
  const previous = useNativeVoiceSession.getState().snapshot;
  if (
    previous &&
    (snapshot.generation < previous.generation || snapshot.revision < previous.revision)
  )
    return false;
  useNativeVoiceSession.setState({ snapshot });
  const store = useVoiceStore.getState();
  const id = snapshot.sessionId;
  const live = snapshot.phase !== 'idle' && snapshot.phase !== 'ended';
  if (id && live && !leaving.has(id)) {
    // A late update from the room we are leaving must not replace a new join intention.
    if (store.room && store.room.sessionId !== id) return true;
    if (!store.room) {
      store.setConnecting({
        sessionId: id,
        postId: snapshot.postId ?? '',
        hostId: snapshot.hostId,
        livekitUrl: '',
        token: '',
        maxParticipants: null,
      });
    }
    const current = useVoiceStore.getState();
    if (snapshot.phase === 'connected') current.setConnected();
    else if (snapshot.phase === 'reconnecting') current.setReconnecting();
    current.setMicEnabled(snapshot.micEnabled);
    current.setMicDenied(snapshot.micDenied);
    current.updateLiveState({
      connected: snapshot.members
        .filter((member) => member.connected)
        .map((member) => member.userId),
      speaking: snapshot.members.filter((member) => member.speaking).map((member) => member.userId),
      muted: snapshot.members.filter((member) => member.muted).map((member) => member.userId),
      profiles: Object.fromEntries(
        snapshot.members.map((member) => [
          member.userId,
          { displayName: member.displayName, avatar: toCallAvatarUrl(member.avatar) },
        ])
      ),
    });
    for (const member of snapshot.members) {
      if (member.volume !== undefined) current.setLocalVolume(member.userId, member.volume);
      if (member.playbackMuted !== undefined)
        current.setLocalMuted(member.userId, member.playbackMuted);
    }
  } else if (id && store.room?.sessionId === id) {
    if (snapshot.endReason === 'personal_call') store.rememberInterruptedRoom(store.room);
    store.reset();
    if (snapshot.errorCode) {
      if (
        snapshot.errorCode !== 'voice.connection.lost' &&
        snapshot.errorCode !== 'voice.connect.failed'
      ) {
        store.forgetVoiceSession();
      }
      store.setError(snapshot.errorCode, id);
    }
  }
  return true;
}

export async function syncNativeVoiceSessionAuth(): Promise<void> {
  if (!(await resolveNativeVoiceSessionSupport())) return;
  const epoch = authEpoch;
  const sync = ++syncEpoch;
  const mark = await nativeSessionMark();
  const userId = await getNativeAuthUserId();
  if (epoch !== authEpoch || !isCurrentNativeSession(mark)) return;
  await nativePlugin().syncAuth();
  const snapshot = await nativePlugin().getState();
  if (epoch !== authEpoch || sync !== syncEpoch || !isCurrentNativeSession(mark)) return;
  if (userId && displayedOwnerUserId && userId !== displayedOwnerUserId) {
    useVoiceStore.getState().forgetVoiceSession();
    useVoiceStore.getState().reset();
  }
  if (userId) displayedOwnerUserId = userId;
  ownerUserId = userId;
  applyNativeVoiceSessionSnapshot(snapshot);
  // Hydrate before lifting the auto-rejoin gate: a live process needs no new join token.
  useNativeVoiceSession.setState({ ready: userId !== null || mark === null });
}

export async function subscribeNativeVoiceSession(): Promise<() => void> {
  if (!(await resolveNativeVoiceSessionSupport())) return () => {};
  const handle = await nativePlugin().addListener('stateChanged', applyNativeVoiceSessionSnapshot);
  return () => void handle.remove();
}

/** Sign-out can begin before startup hydration has populated the browser store. */
export async function readActiveNativeVoiceSessionTarget(): Promise<{
  sessionId: string;
  generation: number;
} | null> {
  if (!(await resolveNativeVoiceSessionSupport())) return null;
  const snapshot = await nativePlugin().getState();
  if (!snapshot.sessionId || snapshot.phase === 'idle' || snapshot.phase === 'ended') return null;
  return { sessionId: snapshot.sessionId, generation: snapshot.generation };
}

export async function connectNativeVoiceSession(room: VoiceRoomInfo): Promise<void> {
  const epoch = authEpoch;
  const current = useVoiceStore.getState();
  if (current.room !== room || !useNativeVoiceSession.getState().ready) return;
  leaving.delete(room.sessionId);
  try {
    const snapshot = await nativePlugin().connect({
      sessionId: room.sessionId,
      postId: room.postId,
      hostId: room.hostId,
      livekitUrl: room.livekitUrl,
      token: room.token,
      muted: !current.micEnabled,
    });
    if (epoch === authEpoch) applyNativeVoiceSessionSnapshot(snapshot);
  } catch (error) {
    if (epoch === authEpoch && useVoiceStore.getState().room === room) throw error;
  }
}

/** Resolve only after native capture and playback have stopped. */
export async function disconnectNativeVoiceSession(
  sessionId: string,
  generation?: number
): Promise<void> {
  if (!(await resolveNativeVoiceSessionSupport())) return;
  // React reset and Leave can observe the same stop. Never send a second unscoped stop later.
  if (leaving.has(sessionId)) return stopping.get(sessionId);
  leaving.add(sessionId);
  const snapshot = useNativeVoiceSession.getState().snapshot;
  const expectedGeneration =
    generation ?? (snapshot?.sessionId === sessionId ? snapshot.generation : undefined);
  const stop = nativePlugin()
    .disconnect({
      sessionId,
      ...(expectedGeneration !== undefined ? { generation: expectedGeneration } : {}),
    })
    .then((result) => {
      applyNativeVoiceSessionSnapshot(result);
      return undefined;
    })
    .catch((error: unknown) => {
      if (stopping.get(sessionId) === stop) leaving.delete(sessionId);
      throw error;
    })
    .finally(() => {
      if (stopping.get(sessionId) === stop) stopping.delete(sessionId);
    });
  stopping.set(sessionId, stop);
  return stop;
}

const reportCommandFailure = (error: unknown, operation: string) => {
  Sentry.captureException(error, { tags: { scope: 'nativeVoiceSession', operation } });
};

/** Browser state retains its existing path; native state changes only after actual media ack. */
export async function requestVoiceMicrophone(enabled: boolean): Promise<boolean> {
  const store = useVoiceStore.getState();
  if (useNativeVoiceSession.getState().mode === 'web') {
    store.setMicEnabled(enabled);
    return true;
  }
  const sessionId = store.room?.sessionId;
  if (!sessionId) return false;
  try {
    if (!(await resolveNativeVoiceSessionSupport())) {
      if (useVoiceStore.getState().room?.sessionId !== sessionId) return false;
      store.setMicEnabled(enabled);
      return true;
    }
    const before = useNativeVoiceSession.getState().snapshot;
    if (!before || before.sessionId !== sessionId || before.phase !== 'connected') return false;
    const epoch = authEpoch;
    const result = await nativePlugin().setMuted({
      sessionId,
      generation: before.generation,
      muted: !enabled,
    });
    if (epoch !== authEpoch || useVoiceStore.getState().room?.sessionId !== sessionId) return false;
    return applyNativeVoiceSessionSnapshot(result) && result.micEnabled === enabled;
  } catch (error) {
    reportCommandFailure(error, 'microphone');
    return false;
  }
}

export async function setNativeVoiceParticipantAudio(
  userId: string,
  options: { muted?: boolean; volume?: number }
) {
  const store = useVoiceStore.getState();
  const snapshot = useNativeVoiceSession.getState().snapshot;
  if (!snapshot?.sessionId || snapshot.sessionId !== store.room?.sessionId) return;
  const epoch = authEpoch;
  try {
    const result = await nativePlugin().setParticipantAudio({
      sessionId: snapshot.sessionId,
      generation: snapshot.generation,
      userId: userId.split('#')[0],
      muted: options.muted ?? store.localMuted[userId] ?? false,
      volume: options.volume ?? store.localVolumes[userId] ?? 1,
    });
    if (epoch !== authEpoch || useVoiceStore.getState().room?.sessionId !== snapshot.sessionId)
      return;
    if (applyNativeVoiceSessionSnapshot(result) && result.generation === snapshot.generation) {
      const participant = result.members.find((member) => member.userId === userId.split('#')[0]);
      if (!participant) return;
      if (options.muted !== undefined)
        store.setLocalMuted(userId, participant.playbackMuted ?? options.muted);
      if (options.volume !== undefined)
        store.setLocalVolume(userId, participant.volume ?? options.volume);
    }
  } catch (error) {
    reportCommandFailure(error, 'participant-audio');
  }
}
